import Clarity from '@microsoft/clarity';
import * as Sentry from '@sentry/react';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import { setGlobalTrackFunction } from '../utils/analytics';
import { mixpanel } from '../utils/mixpanel';
import { UserContext } from './UserContext';

interface AnalyticsContextValue {
  track: (eventName: string, properties?: Record<string, any>) => void;
  trackPageView: (pageName: string, properties?: Record<string, any>) => void;
  setUserProperty: (property: string, value: any) => void;
  setUserProperties: (properties: Record<string, any>) => void;
  setSessionTag: (key: string, value: string) => void;
  reset: () => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | undefined>(
  undefined,
);

interface AnalyticsProviderProps {
  children: ReactNode;
}

export const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  const { selectedAccount } = useContext(UserContext);
  const isProduction = import.meta.env.MODE === 'production';

  // Base properties that should be included with every event
  const baseProperties = useMemo(() => {
    const props: Record<string, any> = {
      environment: import.meta.env.MODE,
    };

    // Auto-include account_id if available
    if (selectedAccount?.id) {
      props.account_id = selectedAccount.id;
      props.account_name = selectedAccount.data().name;
    }

    return props;
  }, [selectedAccount]);

  const setSessionTag = useCallback(
    (key: string, value: string) => {
      if (!isProduction && !import.meta.env.VITE_ENABLE_ANALYTICS_IN_DEV) {
        console.debug('[ANALYTICS] Set session tag:', key, value);
        return;
      }

      try {
        Clarity.setTag(key, value);
      } catch (error) {
        console.error('[ANALYTICS] Failed to set Clarity tag:', key, error);
      }

      try {
        mixpanel.register({ [key]: value });
      } catch (error) {
        console.error(
          '[ANALYTICS] Failed to register Mixpanel property:',
          key,
          error,
        );
      }

      try {
        Sentry.setTag(key, value);
      } catch (error) {
        console.error('[ANALYTICS] Failed to set Sentry tag:', key, error);
      }
    },
    [isProduction],
  );

  // Tag session with account properties when account changes
  useEffect(() => {
    if (selectedAccount?.id) {
      setSessionTag('account_id', selectedAccount.id);
      setSessionTag('account_name', selectedAccount.data().name);
    }
  }, [selectedAccount, setSessionTag]);

  const track = useCallback(
    (eventName: string, properties?: Record<string, any>) => {
      // Always log in development
      if (!isProduction) {
        console.debug('[ANALYTICS]', eventName, {
          ...baseProperties,
          url: window.location.href,
          pathname: window.location.pathname,
          ...properties,
        });
      }

      const eventProperties = {
        ...baseProperties,
        url: window.location.href,
        pathname: window.location.pathname,
        ...properties,
      };

      // Push to GTM dataLayer
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: eventName,
          ...eventProperties,
        });
      } catch (error) {
        console.error('[ANALYTICS] Failed to push to GTM dataLayer:', error);
      }

      // Only send to Mixpanel in production or if explicitly enabled
      if (isProduction || import.meta.env.VITE_ENABLE_ANALYTICS_IN_DEV) {
        try {
          mixpanel.track(eventName, {
            ...eventProperties,
          });
        } catch (error) {
          console.error('[ANALYTICS] Failed to track event:', eventName, error);
        }
      }

      // Send to Clarity as a custom event
      try {
        Clarity.event(eventName);
      } catch (error) {
        console.error('[ANALYTICS] Failed to send Clarity event:', error);
      }
    },
    [baseProperties, isProduction],
  );

  // Set the global track function for backward compatibility
  useEffect(() => {
    setGlobalTrackFunction(track);
    return () => {
      setGlobalTrackFunction(() => {});
    };
  }, [track]);

  const trackPageView = useCallback(
    (pageName: string, properties?: Record<string, any>) => {
      track('page_view', {
        page_name: pageName,
        ...properties,
      });
    },
    [track],
  );

  const setUserProperty = useCallback(
    (property: string, value: any) => {
      if (!isProduction && !import.meta.env.VITE_ENABLE_ANALYTICS_IN_DEV) {
        console.debug('[ANALYTICS] Set user property:', property, value);
        return;
      }

      try {
        mixpanel.people.set({ [property]: value });
      } catch (error) {
        console.error(
          '[ANALYTICS] Failed to set user property:',
          property,
          error,
        );
      }
    },
    [isProduction],
  );

  const setUserProperties = useCallback(
    (properties: Record<string, any>) => {
      if (!isProduction && !import.meta.env.VITE_ENABLE_ANALYTICS_IN_DEV) {
        console.debug('[ANALYTICS] Set user properties:', properties);
        return;
      }

      try {
        mixpanel.people.set(properties);
      } catch (error) {
        console.error('[ANALYTICS] Failed to set user properties:', error);
      }
    },
    [isProduction],
  );

  const reset = useCallback(() => {
    if (!isProduction && !import.meta.env.VITE_ENABLE_ANALYTICS_IN_DEV) {
      console.debug('[ANALYTICS] Reset');
      return;
    }

    try {
      mixpanel.reset();
    } catch (error) {
      console.error('[ANALYTICS] Failed to reset:', error);
    }
  }, [isProduction]);

  const value = useMemo(
    () => ({
      track,
      trackPageView,
      setUserProperty,
      setUserProperties,
      setSessionTag,
      reset,
    }),
    [
      track,
      trackPageView,
      setUserProperty,
      setUserProperties,
      setSessionTag,
      reset,
    ],
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
